import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, EmptyState, SegmentedControl, StatusBadge } from '@/components/UI';
import ActionMenu, { type ActionMenuItem } from './ActionMenu';
import { describeRunLenses, getRunStatusTone, isIdeaLabRunActive, RUN_STATUS_LABELS, type IdeaLabRunSummary } from '../ideaLabUtils';
import {
    buildRerunIndex,
    describeRunWindowShort,
    describeRunWorkUnits,
    describeVariantCount,
    filterStudyRuns,
    formatWallClockDate,
    formatWallClockShort,
    type RunListFilter
} from '../studyView';

interface StudyRunsTableProps {
    runs: IdeaLabRunSummary[];
    studyIsOpen: boolean;
    onNewRun: () => void;
    onRunAgain: (run: IdeaLabRunSummary) => void;
    onCancel: (run: IdeaLabRunSummary) => void;
}

/**
 * The study's runs, newest first. Failed and cancelled attempts are hidden by default; when a
 * failed run was retried with identical settings, its row links the run that replaced it.
 */
const StudyRunsTable = ({ runs, studyIsOpen, onNewRun, onRunAgain, onCancel }: StudyRunsTableProps) => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<RunListFilter>('current');
    const rerunIndex = useMemo(() => buildRerunIndex(runs), [runs]);
    const currentCount = useMemo(() => filterStudyRuns(runs, 'current').length, [runs]);
    const visibleRuns = useMemo(() => filterStudyRuns(runs, filter), [runs, filter]);
    const hiddenCount = runs.length - currentCount;

    if (runs.length === 0) {
        return (
            <EmptyState
                action={
                    studyIsOpen ? (
                        <Button variant="primary" onClick={onNewRun}>
                            Queue a run
                        </Button>
                    ) : undefined
                }
                description="Freeze at least one variant, then queue a run to see fixed-horizon edge versus SPY and random entries."
                size="sm"
                title="No runs yet"
            />
        );
    }

    const menuItems = (run: IdeaLabRunSummary): ActionMenuItem[] => [
        ...(run.status === 'completed'
            ? [
                  {
                      label: 'Visual Validator',
                      description: 'Replay entries on a chart',
                      onSelect: () => void navigate(`/user/ideaLab/run/${run.id}/review`)
                  }
              ]
            : []),
        ...(studyIsOpen && !isIdeaLabRunActive(run)
            ? [
                  {
                      label: 'Run again with changes',
                      description: 'Open the New run form with every setting of this run filled in',
                      onSelect: () => onRunAgain(run)
                  }
              ]
            : []),
        ...(isIdeaLabRunActive(run)
            ? [{ label: 'Cancel run', description: 'Drop its pending work', onSelect: () => onCancel(run), tone: 'danger' as const }]
            : [])
    ];

    return (
        <div className="space-y-3">
            {hiddenCount > 0 && (
                <SegmentedControl
                    ariaLabel="Runs shown"
                    options={[
                        { value: 'current', label: `Completed and in progress (${currentCount})` },
                        { value: 'all', label: `All, including ${hiddenCount} failed or cancelled` }
                    ]}
                    value={filter}
                    onChange={setFilter}
                />
            )}

            <div className="overflow-x-auto rounded-md border border-zinc-800 md:overflow-visible [&_th:first-child]:rounded-tl-md [&_th:last-child]:rounded-tr-md">
                <table className="w-full min-w-[820px] text-sm">
                    <thead>
                        <tr className="bg-[#131416] text-left text-[11px] uppercase tracking-wide text-zinc-400">
                            <th className="px-3 py-2 font-medium">Run</th>
                            <th className="px-3 py-2 font-medium">Status</th>
                            <th className="px-3 py-2 font-medium">Window (Eastern)</th>
                            <th className="px-3 py-2 font-medium">Queued</th>
                            <th className="px-3 py-2">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRuns.map((run) => {
                            const rerun = rerunIndex.get(run.id) ?? null;
                            const workUnits = describeRunWorkUnits(run);

                            return (
                                <tr className="border-t border-zinc-800 align-top hover:bg-zinc-800/30" data-run-id={run.id} key={run.id}>
                                    <td className="px-3 py-2">
                                        <Link className="font-medium text-blue-300 hover:text-blue-200" to={`/user/ideaLab/run/${run.id}`}>
                                            {run.label}
                                        </Link>
                                        <div className="text-xs text-zinc-500">
                                            #{run.id} · {run.primaryDuration} bars · {describeVariantCount(run.variantIds.length)} ·{' '}
                                            {describeRunLenses(run)}
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2">
                                        <StatusBadge
                                            pulse={isIdeaLabRunActive(run)}
                                            status={run.status}
                                            tone={getRunStatusTone(run.status)}
                                        >
                                            {RUN_STATUS_LABELS[run.status]}
                                        </StatusBadge>
                                        {workUnits && <div className="mt-1 text-xs text-zinc-500">{workUnits}</div>}
                                        {rerun && (
                                            <div className="mt-1 text-xs text-zinc-500">
                                                Rerun as{' '}
                                                <Link className="text-blue-300 hover:text-blue-200" to={`/user/ideaLab/run/${rerun.id}`}>
                                                    #{rerun.id}
                                                </Link>
                                            </div>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-zinc-300">
                                        {describeRunWindowShort(run)}
                                        <div className="text-xs text-zinc-500">
                                            {run.walkForwardStartDateTime
                                                ? `walk-forward from ${formatWallClockDate(run.walkForwardStartDateTime)}`
                                                : 'no walk-forward window'}
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-xs text-zinc-400">
                                        {formatWallClockShort(run.queuedAt ?? run.createdAt)}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex justify-end">
                                            <ActionMenu items={menuItems(run)} label={`Actions for run #${run.id}`} />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default StudyRunsTable;
